import type { AdoptionReport, DegradeReason } from "@visorxr/tracker-planar";
import type { BackendId, TrackingState } from "@visorxr/core";
import type { CameraObservation, FrameObservation, SampleObservation, TrackingObservation } from "../backends/tracking-observation";

/** A histogram's shape, carried in the envelope so a stored row reads without the build that wrote it. */
export interface Bins {
  min: number;
  width: number;
  /** Bin count including the last one, which absorbs everything past the final edge. */
  count: number;
}

export interface DeviceInfo {
  ua: string;
  model: string | null;
  platform: string | null;
  platformVersion: string | null;
  maxTouchPoints: number;
  screen: { width: number; height: number };
  dpr: number;
  cores: number | null;
  memoryGb: number | null;
}

export interface Envelope {
  v: 1;
  sessionId: string;
  startedAt: number;
  build: string;
  campaignId: string | null;
  exp: string | null;
  backend: BackendId | "quicklook" | null;
  supported: string[];
  motion: "ok" | "denied" | null;
  markers: number | null;
  device: DeviceInfo | null;
  /** The stream the UA granted, which the requested constraints only suggested. */
  camera: { width: number; height: number; frameRate: number | null } | null;
  /** Whether the platform exposes getVideoPlaybackQuality — without it the frame-delivery watch
   *  is inert, and zero video-stalled events would otherwise be indistinguishable from health. */
  videoQualityApi: boolean | null;
  /** How overlay pixels reach the screen: "direct" GL upload, the canvas "relay" (the WKWebView
   *  upload failure), or "dead" when the relay's canvas machinery failed operationally (the
   *  direct texture stays bound as nothing-better-to-show); null before any surface decided. */
  videoTexturePath: "direct" | "relay" | "dead" | null;
  bins: Record<string, Bins>;
}

export type SessionEvent =
  | { kind: "started"; t: number; backend: Envelope["backend"] }
  | { kind: "acquired"; t: number; target: number; afterLostMs: number | null }
  | {
      kind: "degraded";
      t: number;
      target: number;
      reason: DegradeReason;
      ratio: number;
      transferErrorPx: number | null;
      ncc: number | null;
      l3Confirmed: boolean;
      angularSpeedDps: number | null;
      trackedForMs: number;
      preroll?: number[];
    }
  | { kind: "recovered"; t: number; target: number; limitedForMs: number }
  | { kind: "lost"; t: number; target: number; limitedForMs: number }
  | { kind: "handover"; t: number; from: number; to: number }
  | { kind: "l3-confirmed"; t: number; target: number; sinceAcquireMs: number; ncc: number; rejectedBefore: number }
  | {
      kind: "adoption";
      t: number;
      target: number;
      outcome: AdoptionReport["outcome"];
      /** Both null when no verification ran — nothing was measured, so nothing is fabricated. */
      ncc: number | null;
      rejection: string | null;
    }
  | { kind: "geometry-stale"; t: number; stale: boolean }
  | { kind: "hidden"; t: number }
  | { kind: "visible"; t: number }
  | { kind: "error"; t: number; code: string }
  | { kind: "captured"; t: number }
  | { kind: "audio-blocked"; t: number }
  | { kind: "unmuted"; t: number }
  | { kind: "video-stalled"; t: number; target: number }
  | { kind: "video-recovered"; t: number; target: number };

export type EventKind = SessionEvent["kind"];

export type LifecycleEvent =
  | { kind: "started"; backend: Envelope["backend"] }
  | { kind: "hidden" }
  | { kind: "visible" }
  | { kind: "error"; code: string }
  | { kind: "captured" }
  | { kind: "audio-blocked" }
  | { kind: "unmuted" }
  /** The named poster's video pipeline played audio without one decoded frame, and was rebuilt. */
  | { kind: "video-stalled"; target: number }
  /** Frames arrived after a rebuild — stalled-without-recovered is the posters the heal failed. */
  | { kind: "video-recovered"; target: number };

export interface Totals {
  frames: number;
  states: Partial<Record<TrackingState, number>>;
  degrades: Partial<Record<DegradeReason, number>>;
  /** Every transition, whether or not its event survived the caps. */
  transitions: Partial<Record<EventKind, number>>;
  droppedEvents: number;
  prerolls: number;
  l3: { confirmed: number; rejected: number };
  /** Every adoption by fate, exact where the event cap is not — the door's own scoreboard. */
  adoptions: Record<AdoptionReport["outcome"], number>;
  hist: Record<string, number[]>;
}

/**
 * Bin edges sit on the thresholds they exist to judge, so "below `minNcc`" is a boundary rather
 * than an interpolation. A test pins that against the config, which makes a threshold change a
 * conscious decision about these bins rather than a silent shift of what the data means.
 */
const BIN_SPECS: Record<string, Bins> = {
  ratio: { min: 0, width: 0.05, count: 20 },
  transferErrorPx: { min: 0, width: 0.25, count: 24 },
  // NCC runs from -1: an anti-correlated match (a wrong lock, inverted contrast) is a different
  // finding from an uncorrelated one, and clamping the negatives away would merge them.
  nccConfirmed: { min: -1, width: 0.05, count: 40 },
  nccRejected: { min: -1, width: 0.05, count: 40 },
  // Same shape as the per-frame pair so the three overlay: where refusals cluster against
  // minAdoptionNcc is a distribution question, and the capped adoption events cannot carry it.
  nccAdoption: { min: -1, width: 0.05, count: 40 },
  pipelineMs: { min: 0, width: 1, count: 32 },
  // Wider bins than pipelineMs: a readback contends with whatever else holds the GPU, so its tail
  // runs well past the range a pipeline step occupies.
  captureMs: { min: 0, width: 2, count: 32 },
  frameIntervalMs: { min: 0, width: 4, count: 32 },
  // Deliberately the same shape as frameIntervalMs, so the two overlay directly. What that overlay
  // does and does not establish is docs/17 §3, and only there: a second copy of that reading is a
  // second thing that has to stay true.
  sampleIntervalMs: { min: 0, width: 4, count: 32 },
  angularSpeedDps: { min: 0, width: 10, count: 24 },
  angularSpeedAtDegrade: { min: 0, width: 10, count: 24 },
};

/** How much of the run-up to a degrade is kept: ~1 s at camera rate. */
export const PREROLL_FRAMES = 32;
/** Fields per preroll frame: dt, ratio, ncc, transferErrorPx, angularSpeedDps. */
const PREROLL_FIELDS = 5;
/** Absent measurements need a value the ring can hold that no measurement can produce; ncc alone
 * reaches -1, so absence sits below it. */
const ABSENT = -2;
export const MAX_PREROLLS = 10;

/**
 * Per kind and for the whole session, so a flapping track can neither crowd out the one `lost` or
 * `error` that explains it nor grow the session without bound. Counts in `totals.transitions` stay
 * exact regardless of what the caps drop, and `droppedEvents` says how much was refused.
 */
export const EVENT_CAPS: Record<EventKind, number> = {
  started: 2,
  acquired: 50,
  degraded: 50,
  recovered: 50,
  lost: 50,
  handover: 20,
  "l3-confirmed": 20,
  adoption: 50,
  "geometry-stale": 10,
  hidden: 10,
  visible: 10,
  error: 20,
  captured: 20,
  "audio-blocked": 10,
  unmuted: 10,
  "video-stalled": 10,
  "video-recovered": 10,
};

/** What the recorder is holding about the track it is watching. */
interface TrackState {
  target: number;
  acquiredAtMs: number;
  degradedSinceMs: number | null;
  lostAtMs: number | null;
  l3Confirmed: boolean;
  l3RejectedBefore: number;
  trackingRun: number;
}

/**
 * Turns per-frame observations into a fixed-size session record: counters and histograms for the
 * distributions, timestamped events for the transitions, and nothing retained per frame.
 *
 * Pure by construction — no DOM, no timers, no I/O. It is fed observations and drained by the
 * transport, which is what makes the whole state machine testable without a browser.
 */
export class SessionRecorder {
  private envelope: Envelope;
  private readonly totals: Totals;
  private readonly hist: Record<string, Uint32Array> = {};
  private pending: SessionEvent[] = [];
  /** Kept across drains: the caps bound the session, not the chunk that happens to carry it. */
  private readonly sentByKind: Partial<Record<EventKind, number>> = {};
  private track: TrackState | null = null;
  private lastFrameMs: number | null = null;
  private lastSampleMs: number | null = null;
  private lastLostAtMs: number | null = null;
  private prerollsUsed = 0;
  /** Preallocated ring: the steady state allocates nothing per frame. */
  private readonly preroll = new Float32Array(PREROLL_FRAMES * PREROLL_FIELDS);
  private prerollHead = 0;
  private prerollFilled = 0;

  constructor(envelope: Envelope) {
    this.envelope = { ...envelope, bins: { ...BIN_SPECS } };
    for (const [name, bins] of Object.entries(BIN_SPECS)) this.hist[name] = new Uint32Array(bins.count);
    this.totals = {
      frames: 0,
      states: {},
      degrades: {},
      transitions: {},
      droppedEvents: 0,
      prerolls: 0,
      l3: { confirmed: 0, rejected: 0 },
      adoptions: { refined: 0, raw: 0, refused: 0 },
      hist: {},
    };
  }

  setEnvelope(patch: Partial<Envelope>): void {
    this.envelope = { ...this.envelope, ...patch };
  }

  get pendingEvents(): number {
    return this.pending.length;
  }

  observe(observation: TrackingObservation): void {
    if (observation.kind === "geometry-stale") {
      this.emit({ kind: "geometry-stale", t: observation.tMs, stale: observation.stale });
      // A geometry change stands every target down, so the track that resumes is a new one — not a
      // loss, and not a hand-over.
      this.forgetTrack();
      return;
    }
    if (observation.kind === "sample") {
      this.observeSample(observation);
      return;
    }
    if (observation.kind === "camera") {
      this.observeCamera(observation);
      return;
    }
    this.observeFrame(observation);
  }

  record(event: LifecycleEvent, tMs: number): void {
    if (event.kind === "started") this.emit({ kind: "started", t: tMs, backend: event.backend });
    else if (event.kind === "error") this.emit({ kind: "error", t: tMs, code: event.code });
    else if (event.kind === "hidden") {
      this.emit({ kind: "hidden", t: tMs });
      // The page stops being fed frames; whatever it locked onto is not the same lock on return.
      this.forgetTrack();
    } else if (event.kind === "video-stalled" || event.kind === "video-recovered") {
      // The finding is per poster: without the index, a session that visits several posters cannot
      // say which one played sound over nothing.
      this.emit({ kind: event.kind, t: tMs, target: event.target });
    } else this.emit({ kind: event.kind, t: tMs });
  }

  drain(): { envelope: Envelope; totals: Totals; events: SessionEvent[] } {
    const events = this.pending;
    this.pending = [];
    return {
      envelope: { ...this.envelope },
      // A snapshot: the transport serializes on its own schedule, and later frames must not rewrite
      // what it was handed.
      totals: {
        ...this.totals,
        states: { ...this.totals.states },
        degrades: { ...this.totals.degrades },
        transitions: { ...this.totals.transitions },
        l3: { ...this.totals.l3 },
        adoptions: { ...this.totals.adoptions },
        hist: Object.fromEntries(Object.entries(this.hist).map(([name, counts]) => [name, Array.from(counts)])),
      },
      events,
    };
  }

  /**
   * How often the loop asked, as opposed to how often the camera answered.
   *
   * Nothing is counted per poll but the gap: this arrives at display rate, which is an order of
   * magnitude more often than a frame, and a session must not grow with it.
   */
  private observeSample(observation: SampleObservation): void {
    const intervalMs = this.lastSampleMs === null ? null : observation.tMs - this.lastSampleMs;
    this.lastSampleMs = observation.tMs;
    if (intervalMs !== null && intervalMs > 0) this.bucket("sampleIntervalMs", intervalMs);
  }

  /** Envelope, not histogram: one grant per session, and the reader needs it beside every reading. */
  private observeCamera(observation: CameraObservation): void {
    this.setEnvelope({
      camera: { width: observation.width, height: observation.height, frameRate: observation.frameRate },
    });
  }

  private observeFrame(frame: FrameObservation): void {
    this.totals.frames++;
    this.totals.states[frame.state] = (this.totals.states[frame.state] ?? 0) + 1;
    // Read once, before anything advances the clock: both the cadence histogram and the preroll
    // describe the same gap, and computing it twice let the second reader see zero.
    const intervalMs = this.lastFrameMs === null ? null : frame.tMs - this.lastFrameMs;
    this.lastFrameMs = frame.tMs;
    this.measure(frame, intervalMs);

    if (frame.l3) {
      if (frame.l3.confirmed) this.totals.l3.confirmed++;
      else this.totals.l3.rejected++;
    }

    if (frame.adoption) {
      this.totals.adoptions[frame.adoption.outcome]++;
      if (frame.adoption.verdict) this.bucket("nccAdoption", frame.adoption.verdict.ncc);
      this.emit({
        kind: "adoption",
        t: frame.tMs,
        target: frame.targetIndex,
        outcome: frame.adoption.outcome,
        ncc: frame.adoption.verdict?.ncc ?? null,
        rejection: frame.adoption.verdict?.rejection ?? null,
      });
    }

    if (frame.state === "tracking") this.onTracking(frame, intervalMs);
    else this.onNotTracking(frame);
  }

  private onTracking(frame: FrameObservation, intervalMs: number | null): void {
    if (this.track && this.track.target !== frame.targetIndex) {
      this.emit({ kind: "handover", t: frame.tMs, from: this.track.target, to: frame.targetIndex });
      this.track = null;
    }
    if (!this.track) {
      this.track = {
        target: frame.targetIndex,
        acquiredAtMs: frame.tMs,
        degradedSinceMs: null,
        lostAtMs: null,
        l3Confirmed: false,
        l3RejectedBefore: 0,
        trackingRun: 0,
      };
      const afterLostMs = this.lastLostAtMs === null ? null : frame.tMs - this.lastLostAtMs;
      // Consumed, not merely read: only the lock that directly follows a loss measures from it, or
      // a later hand-over would report the age of an unrelated loss as its re-lock time.
      this.lastLostAtMs = null;
      this.emit({ kind: "acquired", t: frame.tMs, target: frame.targetIndex, afterLostMs });
    } else if (this.track.degradedSinceMs !== null) {
      this.emit({
        kind: "recovered",
        t: frame.tMs,
        target: this.track.target,
        limitedForMs: frame.tMs - this.track.degradedSinceMs,
      });
      this.track.degradedSinceMs = null;
      // A recovery starts a new contiguous run. Without this a long run, one degrade, a single
      // recovered frame and a second degrade would attach a "run-up" made of frames from before the
      // first failure — and would let a flapping track spend the whole preroll budget, which is the
      // thing the run-length guard exists to prevent. The ring needs no reset of its own: nothing
      // can be taken until PREROLL_FRAMES more frames arrive, by which point every slot is fresh.
      this.track.trackingRun = 0;
    }

    this.track.trackingRun++;
    this.pushPreroll(frame, intervalMs);
    this.noteL3(frame);
  }

  private onNotTracking(frame: FrameObservation): void {
    const track = this.track;
    if (!track) return;

    if (frame.state === "limited") {
      // Counted every frame, emitted once: the counter is a time share, the event is a transition.
      this.countDegradeReason(frame);
      if (track.degradedSinceMs === null) {
        track.degradedSinceMs = frame.tMs;
        this.emitDegraded(frame, track);
      }
      return;
    }

    this.countDegradeReason(frame);
    // A track can be abandoned straight from `tracking` — the evidence gates skip the hold — and
    // the abandon's reason must still surface as a `degraded` event, not only as a counter.
    if (frame.degradeReason !== null && track.degradedSinceMs === null) this.emitDegraded(frame, track);
    // "lost" and a restart back to "initializing" are the same fact for a session: the track that
    // was being watched is gone, and the next lock is a new one.
    this.emit({
      kind: "lost",
      t: frame.tMs,
      target: track.target,
      limitedForMs: track.degradedSinceMs === null ? 0 : frame.tMs - track.degradedSinceMs,
    });
    this.lastLostAtMs = frame.tMs;
    this.track = null;
    this.resetPreroll();
  }

  private emitDegraded(frame: FrameObservation, track: TrackState): void {
    // Only the frame the track broke on: the stretch that follows is the aftermath, and counting it
    // would bury the reading under however long the overlay sat there.
    this.bucket("angularSpeedAtDegrade", frame.angularSpeedDps);

    const preroll = this.takePreroll(track);
    this.emit({
      kind: "degraded",
      t: frame.tMs,
      target: track.target,
      reason: frame.degradeReason ?? "low-ratio",
      ratio: frame.trackedRatio,
      transferErrorPx: frame.transferErrorPx,
      ncc: frame.l3?.ncc ?? null,
      l3Confirmed: track.l3Confirmed,
      angularSpeedDps: frame.angularSpeedDps,
      trackedForMs: frame.tMs - track.acquiredAtMs,
      ...(preroll ? { preroll } : {}),
    });
  }

  /**
   * Attributable past the event caps: the counter never drops what the cap refuses to send. Counted
   * only while a track is held, so the population is "frames that degraded a lock" rather than
   * every frame without one.
   */
  private countDegradeReason(frame: FrameObservation): void {
    if (frame.degradeReason) {
      this.totals.degrades[frame.degradeReason] = (this.totals.degrades[frame.degradeReason] ?? 0) + 1;
    }
  }

  private noteL3(frame: FrameObservation): void {
    const track = this.track;
    if (!track || !frame.l3) return;
    if (!frame.l3.confirmed) {
      if (!track.l3Confirmed) track.l3RejectedBefore++;
      return;
    }
    if (track.l3Confirmed) return;
    track.l3Confirmed = true;
    this.emit({
      kind: "l3-confirmed",
      t: frame.tMs,
      target: track.target,
      sinceAcquireMs: frame.tMs - track.acquiredAtMs,
      ncc: frame.l3.ncc,
      rejectedBefore: track.l3RejectedBefore,
    });
  }

  private measure(frame: FrameObservation, intervalMs: number | null): void {
    // Only frames that had a track: a searching or lost frame reports a hard zero, which would pile
    // into the same bottom bin as "the points barely survived" and make the two indistinguishable.
    if (frame.state === "tracking" || frame.state === "limited") this.bucket("ratio", frame.trackedRatio);
    this.bucket("transferErrorPx", frame.transferErrorPx);
    this.bucket("captureMs", frame.captureMs);
    this.bucket("pipelineMs", frame.pipelineMs);
    this.bucket("angularSpeedDps", frame.angularSpeedDps);
    if (frame.l3) this.bucket(frame.l3.confirmed ? "nccConfirmed" : "nccRejected", frame.l3.ncc);

    // A hand-over's second observation describes the same camera frame, so its zero gap is not a
    // cadence reading.
    if (intervalMs !== null && intervalMs > 0) this.bucket("frameIntervalMs", intervalMs);
  }

  private bucket(name: string, value: number | null): void {
    if (value === null || !Number.isFinite(value)) return;
    const bins = BIN_SPECS[name];
    const counts = this.hist[name];
    const index = Math.floor((value - bins.min) / bins.width);
    counts[Math.max(0, Math.min(bins.count - 1, index))]++;
  }

  private pushPreroll(frame: FrameObservation, intervalMs: number | null): void {
    const at = this.prerollHead * PREROLL_FIELDS;
    this.preroll[at] = intervalMs ?? 0;
    this.preroll[at + 1] = frame.trackedRatio;
    this.preroll[at + 2] = frame.l3 ? frame.l3.ncc : ABSENT;
    this.preroll[at + 3] = frame.transferErrorPx ?? ABSENT;
    this.preroll[at + 4] = frame.angularSpeedDps ?? ABSENT;
    this.prerollHead = (this.prerollHead + 1) % PREROLL_FRAMES;
    this.prerollFilled = Math.min(this.prerollFilled + 1, PREROLL_FRAMES);
  }

  private resetPreroll(): void {
    this.prerollHead = 0;
    this.prerollFilled = 0;
  }

  /**
   * The run-up is the only thing that separates a slide from a blur — ratio flat while ncc falls,
   * against both falling together. Kept for degrades with a real run behind them, and only a few
   * per session: a flapping track would otherwise spend the budget on nothing.
   */
  private takePreroll(track: TrackState): number[] | null {
    if (track.trackingRun < PREROLL_FRAMES || this.prerollFilled < PREROLL_FRAMES) return null;
    if (this.prerollsUsed >= MAX_PREROLLS) return null;
    this.prerollsUsed++;
    this.totals.prerolls++;

    const out: number[] = [];
    for (let i = 0; i < PREROLL_FRAMES; i++) {
      const at = ((this.prerollHead + i) % PREROLL_FRAMES) * PREROLL_FIELDS;
      for (let field = 0; field < PREROLL_FIELDS; field++) {
        out.push(Number(this.preroll[at + field].toFixed(3)));
      }
    }
    return out;
  }

  /** Forget the track without calling it a loss — the discontinuity was the page's, not the poster's. */
  private forgetTrack(): void {
    this.track = null;
    this.resetPreroll();
    this.lastFrameMs = null;
    // rAF stops in a hidden tab, so the gap across a backgrounded stretch describes the page
    // lifecycle rather than a stalled loop.
    this.lastSampleMs = null;
    // The discontinuity was the page's, so nothing that re-locks afterwards locked "after a loss".
    this.lastLostAtMs = null;
  }

  private emit(event: SessionEvent): void {
    this.totals.transitions[event.kind] = (this.totals.transitions[event.kind] ?? 0) + 1;
    const sent = this.sentByKind[event.kind] ?? 0;
    if (sent >= EVENT_CAPS[event.kind]) {
      this.totals.droppedEvents++;
      return;
    }
    this.sentByKind[event.kind] = sent + 1;
    this.pending.push(event);
  }
}
